import type { GetError } from "@dealer-center/core-components";
import { defaultDepartments } from "./defaultDepartments";
import { type ErrorResponse } from "@dealer-center/ddc-pages-departments-interface";
import { HTTPError } from "ky";

/**
 * Validates department label to prevent duplicates and name collisions.
 *
 * @param existingTypes - Array of existing department types (uppercase)
 * @param defaultDepartmentTypes - Array of pre-configured department types (uppercase)
 * @returns Validation function compatible with TextInput's getError prop
 */
export const validateDepartment = (
  existingTypes: string[],
  defaultDepartmentTypes: string[]
): GetError => {
  return (_label, value) => {
    // Allow empty values
    if (!value || value.trim().length === 0) {
      return {
        triggered: false,
        validationState: {
          isValid: true,
          type: "success"
        }
      };
    }

    const normalizedValue = value.trim();

    if (normalizedValue.length > 50) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Department name cannot exceed 50 characters"
        }
      };
    }

    // Validate allowed characters (letters, numbers, apostrophe, spaces, hyphens)
    const allowedCharsRegex = /^[a-zA-Z0-9'\s-]+$/;
    if (!allowedCharsRegex.test(normalizedValue)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message:
            "Only letters, numbers, apostrophes, hyphens, and spaces are allowed"
        }
      };
    }

    // Generate the type from the label
    const generatedType = labelToType(normalizedValue);

    // Ensure the generated type is non-empty and contains at least one letter or number
    // (e.g. label of only apostrophes, hyphens, or underscores like "---")
    if (!generatedType || /^[-_]+$/.test(generatedType)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Department name must contain at least one letter or number"
        }
      };
    }

    // Check collision with existing departments
    if (existingTypes.includes(generatedType)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Department already exists"
        }
      };
    }

    // Check collision with pre-configured department types
    if (defaultDepartmentTypes.includes(generatedType)) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Cannot use a pre-configured department name"
        }
      };
    }

    // Check collision with pre-configured department labels (case-insensitive)
    const defaultLabels = defaultDepartments.map(d => d.label.toUpperCase());
    if (defaultLabels.includes(normalizedValue.toUpperCase())) {
      return {
        triggered: true,
        validationState: {
          isValid: false,
          type: "error",
          message: "Cannot use a pre-configured department name"
        }
      };
    }

    return {
      triggered: false,
      validationState: {
        isValid: true,
        type: "success"
      }
    };
  };
};

/**
 * Converts a department label to a valid type key.
 * Transforms human-readable labels into consistent uppercase type identifiers.
 *
 * Transformation rules:
 * - Trims leading/trailing whitespace
 * - Converts to uppercase
 * - Replaces spaces with underscores
 * - Removes non-alphanumeric characters (except underscores)
 * - Collapses consecutive underscores
 * - Removes leading/trailing underscores
 *
 * @param label - The department label to convert
 * @returns Uppercase type string suitable for database storage
 *
 * @example
 * labelToType("Body Shop") => "BODY_SHOP"
 * labelToType("  Custom Dept  ") => "CUSTOM_DEPT"
 * labelToType("Mike's Auto") => "MIKES_AUTO"
 * labelToType("Service - Parts") => "SERVICE_-_PARTS"
 * labelToType("test 2' test - test") => "TEST_2_TEST_-_TEST"
 */
export const labelToType = (label: string): string => {
  if (!label) return "";

  return (
    label
      .trim()
      // Strip apostrophes (backend removes them, not replaces)
      .replace(/'/g, "")
      .toUpperCase()
      // Replace spaces with underscores; hyphens are preserved as-is
      .replace(/\s+/g, "_")
      // Remove any remaining non-alphanumeric characters except underscores and hyphens
      .replace(/[^A-Z0-9_-]/g, "")
      // Collapse consecutive underscores
      .replace(/_+/g, "_")
      // Remove leading/trailing underscores
      .replace(/^_+|_+$/g, "")
  );
};

// Department labels can contain letters, numbers, apostrophes, hyphens, and spaces.
// This function ensures the label is properly formatted before conversion to type.
export const sanitizeLabel = (label: string): string =>
  label
    .replace(/[^a-zA-Z0-9'\s-]/g, "") // Remove disallowed characters
    .replace(/\s+/g, " ") // Collapse multiple spaces into one
    .trim(); // Remove leading/trailing whitespace

/**
 * Parses server-side error messages from a ky HTTPError response body.
 * Returns the messages array if available, otherwise a generic fallback message.
 */
export const parseErrorMessages = async (
  error: unknown,
  fallback = "An unexpected error occurred. Please try again."
): Promise<string[]> => {
  if (error instanceof HTTPError) {
    try {
      const body = await error.response.json<ErrorResponse>();
      if (body?.messages?.length > 0) {
        return body.messages;
      }
    } catch {
      // body could not be parsed — fall through to fallback
    }
  }
  return [error instanceof Error ? error.message || fallback : fallback];
};
